import React, { useEffect, useState } from 'react'
import { Box, IconButton, Switch, Tooltip } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import { keyframes } from '@emotion/react'
import { C, tnum } from './tokens'
import { REFRESH_MS } from './model'

const ping = keyframes`
  0% { transform: scale(1); opacity: 0.55; }
  80%, 100% { transform: scale(2.4); opacity: 0; }
`
const spin = keyframes`to { transform: rotate(360deg); }`

function since(ms) {
  const s = Math.max(0, Math.floor(ms / 1000))
  if (s < 5) return 'just now'
  if (s < 60) return `${s}s ago`
  return `${Math.floor(s / 60)}m ago`
}

/** Owns its own 1 s tick so the rest of the page doesn't re-render every second. */
export default function LiveIndicator({ lastUpdated, auto, onAuto, refreshing, onRefresh, offline }) {
  const [, tick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => tick(n => n + 1), 1000)
    return () => clearInterval(id)
  }, [])
  const live = auto && !offline
  const dot = offline ? C.danger : live ? C.brandGreen : C.faint

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, height: 36, pl: 1.5, pr: 0.5, bgcolor: C.card, border: `1px solid ${C.border}`, borderRadius: '10px', fontSize: 12 }}>
      <Box component="span" aria-hidden sx={{ position: 'relative', width: 8, height: 8, flexShrink: 0 }}>
        {live && (
          <Box
            component="span"
            sx={{ position: 'absolute', inset: 0, borderRadius: '50%', bgcolor: dot, animation: `${ping} 1.8s cubic-bezier(0,0,0.2,1) infinite`, '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}
          />
        )}
        <Box component="span" sx={{ position: 'absolute', inset: 0, borderRadius: '50%', bgcolor: dot }} />
      </Box>
      <Box component="span" sx={{ ml: 0.75, fontWeight: 600, color: C.text, whiteSpace: 'nowrap' }}>{offline ? 'Offline' : live ? 'Live' : 'Paused'}</Box>
      <Box component="span" aria-live="polite" sx={{ color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, ...tnum }}>
        · Updated {refreshing ? 'now…' : lastUpdated ? since(Date.now() - lastUpdated) : '—'}
      </Box>
      <Box sx={{ width: '1px', height: 16, bgcolor: C.border, mx: 1, flexShrink: 0 }} />
      <Tooltip title={`Auto-refresh every ${REFRESH_MS / 1000} seconds`} arrow>
        <Box component="label" sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', color: C.muted, whiteSpace: 'nowrap' }}>
          <Switch
            size="small"
            checked={auto}
            onChange={e => onAuto(e.target.checked)}
            inputProps={{ 'aria-label': 'Auto-refresh' }}
            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: C.brandGreen, opacity: 1 } }}
          />
          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Auto</Box>
        </Box>
      </Tooltip>
      <Tooltip title="Refresh now" arrow>
        <span>
          <IconButton size="small" onClick={onRefresh} disabled={refreshing} aria-label="Refresh now" sx={{ ml: 0.5, color: C.text }}>
            <RefreshIcon sx={{ fontSize: 18, animation: refreshing ? `${spin} 0.8s linear infinite` : 'none' }} />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  )
}
